<template>
  <div role="region" aria-labelledby="custom-package-title">
    <!-- Header -->
    <h2
      id="custom-package-title"
      class="text-xl sm:text-2xl lg:text-3xl text-primary tracking-tight leading-tight font-extrabold text-center mt-6 sm:mt-8 px-4">
      {{ $t("customPackage.mentalHeadline") }}
    </h2>
    <p class="text-center text-base sm:text-lg opacity-75 mt-2 px-4 leading-relaxed">
      {{ $t("customPackage.mentalSubline") }}
    </p>
    <p class="text-center text-sm sm:text-base text-accent mt-3 px-4 leading-relaxed">
      <span class="font-semibold">{{ $t("customPackage.limitedLabel") }}</span>
      {{ $t("customPackage.limitedOffer") }}
    </p>
    <!-- Recommendations -->
    <div class="mt-10">
      <TriggeredRecommendationsCard :recommendations="triggeredRecommendationEntries" />
    </div>

    <!-- STEP 1: Plan Selection -->
    <div class="space-y-2 mb-10 mt-12">
      <div class="flex items-center space-x-2">
        <div class="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">
          1
        </div>
        <span class="text-base font-semibold text-primary">{{ $t("customPackage.step1Title") }}</span>
      </div>

      <div class="mx-auto px-1 sm:px-4 lg:px-8 py-4 sm:py-6">
        <PlanSelectionOptionsGroup
          v-for="group in groups"
          :key="group.id"
          :group="group"
          @toggle="toggleOption(group.id, $event)" />
      </div>
    </div>

    <!-- STEP 2: Domain Checker -->
    <div class="space-y-2 mb-10 mt-12">
      <div class="flex items-center space-x-2">
        <div class="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">
          2
        </div>
        <span class="text-base font-semibold text-primary">{{ $t("customPackage.step2Title") }}</span>
      </div>

      <DomainCheckerInput />
    </div>

    <!-- STEP 3: Summary & Order -->
    <div class="space-y-2 mb-10 mt-12">
      <div class="flex items-center space-x-2">
        <div class="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">
          3
        </div>
        <span class="text-base font-semibold text-primary">{{ $t("customPackage.step3Title") }}</span>
      </div>

      <div class="mx-auto max-w-6xl px-1 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        <h3 id="bundle-summary-title" class="text-lg sm:text-xl lg:text-2xl font-semibold mb-4 text-center px-1">
          {{ $t("customPackage.summaryTitle") }}
        </h3>
        <PlanSelectionSummary
          :selectedPlanOptions="combinedPlanOptions"
          :cta-link="signupLink"
          v-model:billing-interval="billingInterval" />
        <FooterNote class="mt-2" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import PlanSelectionOptionsGroup from "@/features/planSelection/components/PlanSelectionOptionsGroup.vue";
import PlanSelectionSummary from "@/features/planSelection/components/PlanSelectionSummary.vue";
import TriggeredRecommendationsCard from "@/features/planSelection/components/TriggeredRecommendationsCard.vue";
import DomainCheckerInput from "@/features/domain-checker-input/DomainCheckerInput.vue";
import { usePlanSelectionViewModel } from "@/features/planSelection/composables/usePlanSelectionViewModel";
import { useDomainSelection } from "@/features/domain-checker-input/composables/useDomainSelection";
import { PlanOption } from "@/features/planSelection/models/PlanOption";
import FooterNote from "@/features/pricing-summary/components/FooterNote.vue";

// domain summary (one-time) item
const domainStore = useDomainSelection();
const { summaryItem } = storeToRefs(domainStore);

// billing interval state
const billingInterval = ref<"monthly" | "yearly">("monthly");

// view-model provides groups, selectedPlanOptions, toggle, signupLink, etc.
const { groups, toggleOption, selectedPlanOptions, signupLink, triggeredRecommendationEntries } =
  usePlanSelectionViewModel(billingInterval);

// Use selected plan options directly (no additional domain products)
const combinedPlanOptions = computed(() => {
  return selectedPlanOptions.value;
});
</script>

<style scoped>
button[role="radio"] {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  min-height: 10rem;
}
</style>
